import styled from "@emotion/styled";
import { capitalize, isEmpty, omit } from "lodash";
import { Component, FC } from "react";
import { connect, useDispatch, useSelector } from "react-redux";

import { Check, Info, Search } from "@livechat/design-system-icons";
import { Button, Heading, Icon, SpacingToken, Text } from "@livechat/design-system-react-components";

import { ActionLink } from "components/links/ActionLink";
import { StyledLink } from "components/links/StyledLink";
import { IFilters } from "constants/filters";
import { HelpCenter } from "constants/help-center";
import { TicketsRoutes } from "constants/tickets/TicketsRoutes";
import { isInIframe } from "helpers/app";
import { getAppRootWithOrigin } from "helpers/url";
import { TicketSilo, getSiloByRoute, siloTabNames } from "interfaces/entities/ticket";
import { IStoreState } from "interfaces/storeState";
import { TicketsViewActions } from "store/views/tickets/actions";
import { getSilo, selectActiveCustomViewId, selectQualifiesForSavingAsCustomView } from "store/views/tickets/selectors";

import { trackSearchSiloTabOpenEvent, trackTicketsSearchClearEvent } from "./analytics";

interface IProps {
  route: TicketsRoutes;
  filters: IFilters;
  searchQuery: string;
  areMailboxesSetUp: boolean;
  canAccessSettings: boolean;
  activeCustomViewId: string;
  qualifiesForSavingAsCustomView: boolean;
  isDataFresh?: boolean;
  onSearchClear(): void;
}

export const Wrapper = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: start;
  text-align: center;
  gap: var(${SpacingToken.Spacing2});
`;

const incompleteSetupUrl = "/settings/email-addresses";
// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const openIncompleteSetupExternalLink = () => {
  window.open(`${getAppRootWithOrigin()}${incompleteSetupUrl}`);
};

const EmptyStateSetupIncomplete: FC<{ canAccessSettings: boolean }> = ({ canAccessSettings }) => {
  const showExternalAppLink = isInIframe();

  return (
    <>
      <Icon source={Info} size="xxxlarge" />
      <Heading>No tickets yet.</Heading>
      <Text>
        {canAccessSettings ? (
          <>
            {showExternalAppLink ? (
              <>
                <ActionLink fontSize="16px" inline onClick={openIncompleteSetupExternalLink}>
                  Follow the instructions
                </ActionLink>{" "}
                to start working with tickets.
              </>
            ) : (
              <>
                <StyledLink to={incompleteSetupUrl}>Follow the instructions</StyledLink> to start working with tickets.
              </>
            )}
          </>
        ) : (
          <>Your Admin must set up email forwarding to HelpDesk first.</>
        )}
      </Text>
    </>
  );
};

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const EmptyStateClearFilters = ({ onSearchClear }: { onSearchClear(): void }) => (
  <>
    <Icon source={Search} size="xxxlarge" />
    <Heading>No matching tickets found</Heading>
    <Text>Clear filters and try again</Text>
    <Button onClick={onSearchClear}>Clear filters</Button>
  </>
);

interface IEmptyStateSearchProps {
  onSearchClear(): void;
  searchQuery: string;
}

const EmptyStateSearch: FC<IEmptyStateSearchProps> = ({
  onSearchClear,
  searchQuery,
}: {
  onSearchClear(): void;
  searchQuery: string;
}) => {
  const dispatch = useDispatch();
  const currentSilo = useSelector(getSilo);

  const handleSiloSet = (silo: TicketSilo): void => {
    trackSearchSiloTabOpenEvent(silo, searchQuery);
    dispatch(TicketsViewActions.setSilo(silo));
  };

  const handleSearchClear = (): void => {
    trackTicketsSearchClearEvent("empty state button");
    onSearchClear();
    dispatch(TicketsViewActions.setSilo(TicketSilo.Tickets));
  };

  const unselectedSilos = [TicketSilo.Tickets, TicketSilo.Archive, TicketSilo.Spam, TicketSilo.Trash].filter(
    silo => silo !== currentSilo
  );

  const unselectedTabs = unselectedSilos.map(silo => siloTabNames[silo]);

  return (
    <>
      <Icon source={Search} size="xxxlarge" />
      <Heading>No matching tickets found</Heading>
      <Text>
        Check out the{" "}
        <Button kind="link-light" onClick={() => handleSiloSet(unselectedSilos[0])}>
          {unselectedTabs[0]}
        </Button>
        ,{" "}
        <Button kind="link-light" onClick={() => handleSiloSet(unselectedSilos[1])}>
          {unselectedTabs[1]}
        </Button>
        , and{" "}
        <Button kind="link-light" onClick={() => handleSiloSet(unselectedSilos[2])}>
          {unselectedTabs[2]}
        </Button>{" "}
        folders,
        <br /> or adjust your search criteria and filter options.
      </Text>
      <Button onClick={handleSearchClear}>Clear and search again</Button>
    </>
  );
};

const OtherSiloEmptyState: FC<{ silo: TicketSilo; filters: IFilters }> = ({ silo, filters }) => {
  const otherFilters = omit(filters, ["silo"]);
  const hasAdditionalFilters = !isEmpty(otherFilters);
  const isArchive = silo === TicketSilo.Archive;
  const isSpam = silo === TicketSilo.Spam;
  const isTrash = silo === TicketSilo.Trash;

  if (hasAdditionalFilters) {
    return (
      <>
        <Icon source={Search} size="xxxlarge" />
        <Heading>No matching tickets found</Heading>
        <Text>Clear filters and try again</Text>
      </>
    );
  }

  return (
    <>
      <Icon source={Info} size="xxxlarge" />
      <Heading>No tickets in {capitalize(silo)}</Heading>
      <Text>
        <Button target="_blank" kind="link-light" href={HelpCenter.ARCHIVE_TRASH_AND_SPAM_FOLDERS}>
          Follow the instructions
        </Button>{" "}
        {isArchive && "to archive your tickets."}
        {isSpam && "to move tickets to Spam."}
        {isTrash && "to move tickets to Trash."}
      </Text>
    </>
  );
};

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const EmptyStateWellDone = () => (
  <>
    <Icon source={Check} size="xxxlarge" />
    <Heading>You&apos;re all caught up!</Heading>
    <Text>No tickets right now.</Text>
  </>
);

export class TicketsEmptyStateComponent extends Component<IProps> {
  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  shouldComponentUpdate(nextProps: IProps) {
    return nextProps.isDataFresh !== false;
  }

  static defaultProps = {
    isDataFresh: false,
  };

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  render() {
    const {
      route,
      areMailboxesSetUp,
      canAccessSettings,
      searchQuery,
      onSearchClear,
      filters,
      qualifiesForSavingAsCustomView,
    } = this.props;

    const hasSearchQuery = searchQuery.length > 0;

    const siloFromRoute = getSiloByRoute(route);
    const isOtherSiloRoute = [TicketSilo.Archive, TicketSilo.Spam, TicketSilo.Trash].includes(siloFromRoute);

    // the forwarding setup is incomplete
    if (!areMailboxesSetUp) {
      return (
        <Wrapper>
          <EmptyStateSetupIncomplete canAccessSettings={canAccessSettings} />
        </Wrapper>
      );
    }

    // only if the search query exists - change silo tab
    if (hasSearchQuery) {
      return (
        <Wrapper>
          <EmptyStateSearch onSearchClear={onSearchClear} searchQuery={searchQuery} />
        </Wrapper>
      );
    }

    // only for silo Archive, Spam and Trash
    if (isOtherSiloRoute) {
      return (
        <Wrapper>
          <OtherSiloEmptyState silo={siloFromRoute} filters={filters} />
        </Wrapper>
      );
    }

    // clear filters and redirect to all
    if (qualifiesForSavingAsCustomView) {
      return (
        <Wrapper>
          <EmptyStateClearFilters onSearchClear={onSearchClear} />
        </Wrapper>
      );
    }

    return (
      <Wrapper>
        <EmptyStateWellDone />
      </Wrapper>
    );
  }
}

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const mapStateToProps = (state: IStoreState) => ({
  activeCustomViewId: selectActiveCustomViewId(state),
  qualifiesForSavingAsCustomView: selectQualifiesForSavingAsCustomView(state),
});

// @ts-expect-error TS(2345): Argument of type 'typeof TicketsEmptyStateComponen... Remove this comment to see the full error message
export const TicketsEmptyState = connect(mapStateToProps)(TicketsEmptyStateComponent);
